/**
 * Expression Evaluator
 *
 * Evaluates JavaScript expression syntax inside `{expression}` bindings.
 * Uses `new Function()` with a controlled scope — same security model as
 * ScriptCompiler (defense-in-depth, not a true sandbox).
 *
 * Expressions support:
 * - Arithmetic: +, -, *, /, %
 * - Comparison: <, >, <=, >=, ==, !=
 * - Logical: &&, ||, !
 * - Ternary: condition ? a : b
 * - Function calls: clamp(0, x, 1), floor(x)
 * - Member access: camera.distance, self.position.x
 * - Identifiers resolve against a provided scope object
 *
 * NOT supported (and rejected by validation):
 * - Assignment (=, +=, etc.)
 * - Statements (if, for, while, etc.)
 * - Object/array literals, arrow functions, new, delete, typeof
 *
 * See docs/canopy/CANOPY_EXPRESSIONS.md for the expression system spec.
 */

import { Logger } from '../../utils/Logger';

// ── Validation ──────────────────────────────────────────

/**
 * Forbidden patterns in expressions — reject anything that goes beyond
 * simple value computation. Checked against the raw expression text.
 */
const FORBIDDEN_EXPR_PATTERNS: [RegExp, string][] = [
    // Assignment (but not == or !=)
    [/(?<![!=<>])=(?!=)/g, 'Assignment is not allowed in expressions'],
    // Statements
    [/\b(if|else|for|while|do|switch|return|throw|try|catch|finally|var|let|const|class|function|import|export)\b/g,
        'Statements are not allowed in expressions'],
    // Constructors and destructors
    [/\bnew\b/g, '`new` is not allowed in expressions'],
    [/\bdelete\b/g, '`delete` is not allowed in expressions'],
    [/\btypeof\b/g, '`typeof` is not allowed in expressions'],
    [/\bvoid\b/g, '`void` is not allowed in expressions'],
    // Global access
    [/\b(window|document|globalThis|process|eval)\b/g, 'Global access is not allowed in expressions'],
    // Template literals
    [/`/g, 'Template literals are not allowed in expressions'],
    // Arrow functions
    [/=>/g, 'Arrow functions are not allowed in expressions'],
    // Object/array literals (but allow member access)
    [/\[/g, 'Array literals/access is not allowed in expressions'],
    [/\{/g, 'Object literals are not allowed in expressions'],
];

/**
 * Validate an expression string for forbidden constructs.
 * @returns Array of violation messages (empty = clean)
 */
export function validateExpression(expr: string): string[] {
    const violations: string[] = [];
    for (const [pattern, message] of FORBIDDEN_EXPR_PATTERNS) {
        pattern.lastIndex = 0;
        if (pattern.test(expr)) {
            violations.push(message);
        }
    }
    return violations;
}

// ── Built-in functions ──────────────────────────────────

/** Math functions available as bare names in expressions */
export const EXPRESSION_BUILTINS: Record<string, (...args: number[]) => number> = {
    // Core math
    floor: Math.floor,
    ceil: Math.ceil,
    round: Math.round,
    abs: Math.abs,
    sign: Math.sign,
    sqrt: Math.sqrt,
    pow: Math.pow,

    // Clamping & interpolation
    min: Math.min,
    max: Math.max,
    clamp: (min: number, x: number, max: number) => Math.max(min, Math.min(max, x)),
    lerp: (a: number, b: number, t: number) => a + (b - a) * t,
    map: (x: number, inMin: number, inMax: number, outMin: number, outMax: number) => {
        const span = inMax - inMin;
        if (span === 0) return outMin;
        return outMin + (outMax - outMin) * ((x - inMin) / span);
    },
    smoothstep: (edge0: number, edge1: number, x: number) => {
        const span = edge1 - edge0;
        if (span === 0) return 0;
        const t = Math.max(0, Math.min(1, (x - edge0) / span));
        return t * t * (3 - 2 * t);
    },

    // Trigonometry
    sin: Math.sin,
    cos: Math.cos,
    tan: Math.tan,
    atan2: Math.atan2,

    // Random
    random: (min?: number, max?: number) => {
        if (min === undefined) return Math.random();
        if (max === undefined) return Math.random() * min;
        return min + Math.random() * (max - min);
    },
};

// ── Compilation cache ───────────────────────────────────

/**
 * Cache of compiled expression functions, keyed by expression source.
 * Avoids repeated `new Function()` calls for the same expression.
 *
 * Bounded in practice: expressions come from authored scene data, so the set
 * of unique expression strings is finite and small per scene.
 */
const _compiledCache = new Map<string, ((scope: Record<string, unknown>) => unknown) | null>();
const _compileErrors = new Map<string, string>();

/**
 * Identifier scans keyed by expression source: a bound property resolves its expression
 * every frame it is read, so without this the same strings are rescanned each frame.
 */
const _identifierCache = new Map<string, readonly string[]>();

/**
 * Compile an expression string into a reusable evaluator function.
 * Returns null if the expression is invalid (logged as warning).
 *
 * The compiled function takes a scope object and evaluates the expression with every
 * name it uses that the scope holds bound once on entry. Any other name is left to the
 * language: a global, or a ReferenceError when evaluation reaches it. That is what
 * `with(scope)` did, without `with`: a function holding it is never optimized, and
 * bound properties run their expressions every frame.
 */
export function compileExpression(expr: string): ((scope: Record<string, unknown>) => unknown) | null {
    const cached = _compiledCache.get(expr);
    if (cached !== undefined) return cached;

    // Validate
    const violations = validateExpression(expr);
    if (violations.length > 0) {
        Logger.warn(`[Expression] Invalid expression "${expr}": ${violations.join(', ')}`);
        _compiledCache.set(expr, null);
        _compileErrors.set(expr, violations.join(', '));
        return null;
    }

    try {
        const fn = compileScoped(expr);
        _compiledCache.set(expr, fn);
        return fn;
    } catch (e) {
        Logger.warn(`[Expression] Failed to compile "${expr}":`, e);
        _compiledCache.set(expr, null);
        _compileErrors.set(expr, e instanceof Error ? e.message : String(e));
        return null;
    }
}

type ScopedBody = (scope: Record<string, unknown>) => unknown;

/**
 * One function body per set of names the scope holds, so a name the scope lacks is
 * never declared and is looked up only if evaluation reaches it (`open ? width : 0`).
 * The no-binding body is built first and doubles as the syntax check.
 */
function compileScoped(expr: string): ScopedBody {
    const names = boundNames(expr);
    const build = (present: (name: string) => boolean): ScopedBody => {
        const held = names.filter(present);
        const bindings = held.length ? `var ${held.map(name => `${name} = $scope[${JSON.stringify(name)}]`).join(', ')};` : '';
        // eslint-disable-next-line @typescript-eslint/no-implied-eval
        return new Function('$scope', `${bindings} return (${expr});`) as ScopedBody;
    };
    const unbound = build(() => false);
    if (names.length === 0) return unbound;
    // Keyed by a bitmask of the names held (a string past 31 names) so the per-frame call allocates nothing.
    const bodies = new Map<number | string, ScopedBody>([[0, unbound]]);
    return (scope) => {
        let mask = 0;
        let wide = '';
        for (let i = 0; i < names.length; i++) {
            if (!(names[i] in scope)) continue;
            if (i < 31) mask |= 1 << i;
            else wide += `${i},`;
        }
        const key = wide ? `${mask}|${wide}` : mask;
        let body = bodies.get(key);
        if (!body) {
            body = build(name => name in scope);
            bodies.set(key, body);
        }
        return body(scope);
    };
}

/** Words that can never be a binding: literals, operators and reserved words. */
const NOT_BINDINGS = new Set([
    'true', 'false', 'null', 'undefined', 'NaN', 'Infinity', 'this', 'in', 'instanceof', 'of', '$scope',
    'break', 'case', 'catch', 'class', 'const', 'continue', 'debugger', 'default', 'delete', 'do', 'else',
    'enum', 'export', 'extends', 'finally', 'for', 'function', 'if', 'import', 'new', 'return', 'super',
    'switch', 'throw', 'try', 'typeof', 'var', 'void', 'while', 'with',
]);

/**
 * The names an expression may read: outside string literals, and not after a member
 * `.` (a spread `...` still binds). Over-reporting is harmless: only names the scope
 * holds are declared, and a declared name the expression never reads costs nothing.
 */
function boundNames(expr: string): string[] {
    const code = expr.replace(/'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"/g, '""');
    const names = new Set<string>();
    for (const match of code.matchAll(/(?<![\w$])(?<!(?<!\.)\.)([A-Za-z_$][\w$]*)/g)) {
        if (!NOT_BINDINGS.has(match[1])) names.add(match[1]);
    }
    return [...names];
}

/** On-demand diagnostics use exactly the same compiler and scope as runtime. */
export function inspectExpression(expr: string, scope: Record<string, unknown>):
    { ok: true; value: unknown } | { ok: false; code: 'invalid-expression' | 'evaluation-error'; message: string } {
    const fn = compileExpression(expr);
    if (!fn) return { ok: false, code: 'invalid-expression', message: _compileErrors.get(expr) ?? 'Expression could not be compiled' };
    try { return { ok: true, value: fn(scope) }; }
    catch (error) { return { ok: false, code: 'evaluation-error', message: error instanceof Error ? error.message : String(error) }; }
}

// ── Evaluation ──────────────────────────────────────────

/**
 * Evaluate a compiled expression against a scope.
 * Returns undefined on error (logged as warning).
 */
export function evaluateExpression(
    fn: (scope: Record<string, unknown>) => unknown,
    scope: Record<string, unknown>,
): unknown {
    try {
        return fn(scope);
    } catch (e) {
        Logger.warn('[Expression] Evaluation error:', e);
        return undefined;
    }
}

// ── Identifier extraction ───────────────────────────────

/**
 * Extract identifier names from an expression string.
 * Used by the scanner to determine which property names an expression depends on.
 *
 * This is a lightweight regex-based extraction — it doesn't parse the expression
 * into an AST, so it may over-report identifiers (e.g. from string literals).
 * That's acceptable: false positives cause unnecessary subscriptions, not errors.
 */
/** Names that are built-in functions or JS keywords, not property references */
const NON_PROPERTY_NAMES = new Set([
    // Built-in functions
    ...Object.keys(EXPRESSION_BUILTINS),
    // JS literals
    'true', 'false', 'null', 'undefined',
    'NaN', 'Infinity',
    // Math constants (if we add them)
    'PI', 'E',
]);

/**
 * Extract property names referenced in an expression.
 * Filters out built-in function names and JS keywords.
 *
 * For dotted access like `camera.distance`, extracts the root identifier `camera`.
 * The expression evaluator handles the member access at runtime.
 */
export function extractExpressionIdentifiers(expr: string): readonly string[] {
    const cached = _identifierCache.get(expr);
    if (cached) return cached;
    const names: string[] = [];
    const re = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(expr)) !== null) {
        const name = m[1];
        if (!NON_PROPERTY_NAMES.has(name)) {
            names.push(name);
        }
    }
    // Deduplicate
    const identifiers = Object.freeze([...new Set(names)]);
    _identifierCache.set(expr, identifiers);
    return identifiers;
}
